import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Sparkles, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { tenantSettingsApi } from '../../services/api';

/**
 * DemoDataCard — shown on Settings → Profile.
 *
 * Surfaces any `is_demo: true` seed records still present in the tenant
 * (added by the Test Drive signup) and offers a one-click "Remove demo data"
 * button. The card hides itself completely when nothing demo remains.
 */
const LABELS = {
  assets: 'sample assets',
  depots: 'demo depots',
  work_orders: 'demo work orders',
  defects: 'demo defects',
  certificates: 'demo certificates',
  inspection_templates: 'demo inspection templates',
  inspections: 'demo inspections',
};

const DemoDataCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = async () => {
    try {
      const { data } = await tenantSettingsApi.getDemoData();
      setStatus(data);
    } catch {
      /* silent — card just hides */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      const { data } = await tenantSettingsApi.removeDemoData();
      toast.success(`Removed ${data.total} demo records.`);
      // Hide the card immediately rather than waiting for a re-fetch — the
      // server has confirmed the delete succeeded.
      setStatus({ total: 0, counts: {} });
      setConfirm(false);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Could not remove demo data.');
    } finally {
      setRemoving(false);
    }
  };

  // Hide card entirely if nothing to clean up (initial fetch + no demo data)
  if (loading || !status || status.total === 0) return null;

  const breakdown = Object.entries(status.counts || {})
    .map(([key, n]) => `${n} ${LABELS[key] || key}`)
    .join(' · ');

  return (
    <Card className="border-[#FFD108]/30 bg-[#FFD108]/[0.04]" data-testid="demo-data-card">
      <CardHeader>
        <CardTitle className="uppercase tracking-wide flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#FFD108]" />
          Demo data
        </CardTitle>
        <CardDescription>
          When you signed up, we pre-loaded sample assets, a work order, an inspection template
          and more so you could explore right away. When you're ready, clean it out in one click —
          your real data won't be touched.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border border-border bg-card/40 px-4 py-3 text-sm">
          <p className="text-muted-foreground text-xs uppercase tracking-[0.2em] font-semibold mb-1">
            Currently in your workspace
          </p>
          <p className="font-medium text-foreground" data-testid="demo-data-breakdown">
            {status.total} demo records — {breakdown}
          </p>
        </div>

        {!confirm ? (
          <Button
            variant="outline"
            onClick={() => setConfirm(true)}
            className="border-red-500/30 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/50"
            data-testid="demo-remove-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Remove demo data
          </Button>
        ) : (
          <div className="rounded-lg border border-red-500/30 bg-red-500/[0.05] p-4 space-y-3" data-testid="demo-confirm">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold">Remove all {status.total} demo records?</p>
                <p className="text-muted-foreground text-xs mt-0.5">
                  This permanently deletes the seed assets, work order, defect, certificate, template
                  and depot. Anything you've created yourself stays put. This can't be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                onClick={() => setConfirm(false)}
                disabled={removing}
                data-testid="demo-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRemove}
                disabled={removing}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="demo-confirm-btn"
              >
                {removing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {removing ? 'Removing…' : 'Yes, remove it'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DemoDataCard;
